import { normalizeGeoLabel } from "./geoLabel";
import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import type { TelanganaDistrictKey } from "../data/telanganaDistricts";
import { TELANGANA_DISTRICTS, isTelanganaDistrictKey } from "../data/telanganaDistricts";

/** One logical row from a TG-style PDF/Excel export. */
export type ParsedLeadRow = {
  fileMandal: string;
  fileVillage: string;
  applicantName: string;
  mobile: string;
  aadhaarDigits: string;
  /** Application number from sheet (e.g. 204852/1075289). */
  applicationRef: string;
  /** Serial column when present (e.g. 1); used for stable id + parsing. */
  applicationSerial: string;
  listType: string;
  applicationStatus: string;
  /** Excel “District” column (per row); PDF rows usually omit this and use document-level district. */
  fileDistrictKey?: TelanganaDistrictKey | null;
};

const APP_CELL = /^(\d+)\s+(\d+\/\d+|\d{4,})$/;
const APP_CELL_SLASH_ONLY = /^(\d+\/\d+)$/;

export type LeadParseStats = {
  fileLineCount: number;
  rowsAccepted: number;
  skippedNoAppToken: number;
  skippedNoMandalVillage: number;
  skippedShortApplicant: number;
  skippedRequiredBlank: number;
  looseLineFallbacks: number;
};

function splitLineToCells(line: string): string[] {
  const t = line.trim();
  if (!t) return [];
  if (t.includes("\t")) return t.split("\t").map((c) => c.trim());
  return t.split(/\s{2,}/).map((c) => c.trim()).filter(Boolean);
}

/**
 * PDFs often split "57" and "279505/1563504" into two cells — merge back for app detection.
 * Also merge "148" + "1002205" (no slash in second part).
 */
function mergeSplitApplicationCells(cells: string[]): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < cells.length) {
    const a = cells[i] ?? "";
    const b = cells[i + 1] ?? "";
    if (/^\d+$/.test(a) && /^\d+\/\d+/.test(b)) {
      out.push(`${a} ${b}`.trim());
      i += 2;
      continue;
    }
    if (/^\d+$/.test(a) && /^\d{5,}$/.test(b) && !b.includes("/")) {
      out.push(`${a} ${b}`.trim());
      i += 2;
      continue;
    }
    out.push(a);
    i += 1;
  }
  return out;
}

function findAppCellIndex(cells: string[]): number {
  for (let i = 0; i < cells.length; i++) {
    if (parseAppCell(cells[i])) return i;
    if (i + 1 < cells.length) {
      const joined = `${cells[i]} ${cells[i + 1]}`.trim();
      if (parseAppCell(joined)) return i;
    }
  }
  return -1;
}

export function parseAppCellFlexible(cell: string): { serial: string; applicationRef: string } | null {
  const t = cell.trim();
  const m1 = t.match(APP_CELL);
  if (m1) return { serial: m1[1], applicationRef: m1[2] };
  const m2 = t.match(APP_CELL_SLASH_ONLY);
  if (m2) return { serial: "0", applicationRef: m2[1] };
  return null;
}

export function districtLineToKey(line: string): TelanganaDistrictKey | null {
  const m = line.trim().match(/^District\s*[-–]\s*(.+)$/i);
  if (!m) return null;
  return districtLabelToKey(m[1]);
}

/** Map a plain district label (e.g. Excel “District” cell “Nagarkurnool”) to a directory key. */
export function districtLabelToKey(raw: string): TelanganaDistrictKey | null {
  const label = normalizeGeoLabel(String(raw ?? "").trim());
  if (!label) return null;
  for (const d of TELANGANA_DISTRICTS) {
    if (normalizeGeoLabel(d.label) === label) return d.key;
    for (const ln of d.lines) {
      if (normalizeGeoLabel(ln) === label) return d.key;
    }
  }
  const compact = label.replace(/\s+/g, "");
  for (const d of TELANGANA_DISTRICTS) {
    if (normalizeGeoLabel(d.label).replace(/\s+/g, "") === compact) return d.key;
  }
  return null;
}

function parseAppCell(cell: string): { serial: string; applicationRef: string } | null {
  return parseAppCellFlexible(cell);
}

/**
 * Parse tabular text from TG-style PDF text extraction (e.g. Amrabad list).
 * Uses "District - …" lines when present; otherwise caller should pass default district for geo matching.
 */
/**
 * When a line has no tabs, try to split using "… serial appNo … name … mobile … aadhaar …".
 * Recovers many PDF.js extractions that glue columns with single spaces.
 */
function tryParseLeadLineLoose(
  line: string,
  ctx: { currentMandal: string; currentVillage: string },
  geo: GeoDirectoryStore | null,
  districtKey: TelanganaDistrictKey | null,
): ParsedLeadRow | null {
  const t = line.trim();
  if (!t || t.includes("\t")) return null;
  const m = t.match(
    /^(?:(.*?)\s+)?(\d+)\s+(\d+\/\d+)\s+(.+?)\s+(\d[\d\s\-]{8,14}\d)\s+(\d[\d\s]{7,16}\d)(?:\s+(.*))?$/u,
  );
  if (!m) {
    const m2 = t.match(/^(?:(.*?)\s+)?(\d+)\s+(\d{5,})\s+(.+?)\s+(\d[\d\s\-]{8,14}\d)\s+(\d[\d\s]{7,16}\d)(?:\s+(.*))?$/u);
    if (!m2) return null;
    const left = (m2[1] ?? "").trim();
    const serial = m2[2];
    const appRef = m2[3];
    const applicantName = m2[4].trim();
    const mobile = m2[5].replace(/\D/g, "").slice(0, 15);
    const aadhaarRaw = m2[6].replace(/\D/g, "").slice(0, 12);
    if (mobile.length < 10 || aadhaarRaw.length < 9) return null;
    const tail = (m2[7] ?? "").trim();
    return buildRowFromLeftAndApp(left, serial, appRef, applicantName, mobile, aadhaarRaw, tail, ctx, geo, districtKey);
  }
  const left = (m[1] ?? "").trim();
  const serial = m[2];
  const appRef = m[3];
  const applicantName = m[4].trim();
  const mobile = m[5].replace(/\D/g, "").slice(0, 15);
  const aadhaarRaw = m[6].replace(/\D/g, "").slice(0, 12);
  if (mobile.length < 10 || aadhaarRaw.length < 9) return null;
  const tail = (m[7] ?? "").trim();
  return buildRowFromLeftAndApp(left, serial, appRef, applicantName, mobile, aadhaarRaw, tail, ctx, geo, districtKey);
}

function buildRowFromLeftAndApp(
  left: string,
  serial: string,
  appRef: string,
  applicantName: string,
  mobile: string,
  aadhaarRaw: string,
  tail: string,
  ctx: { currentMandal: string; currentVillage: string },
  geo: GeoDirectoryStore | null,
  districtKey: TelanganaDistrictKey | null,
): ParsedLeadRow | null {
  if (!applicantName || applicantName.length < 1) return null;
  let fileMandal = ctx.currentMandal;
  let fileVillage = ctx.currentVillage;
  const tokens = left.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2 && geo && districtKey && isTelanganaDistrictKey(districtKey)) {
    const nm = normalizeGeoLabel(tokens[0]);
    const hit = geo.tgMandalsForDistrict(districtKey).find((x) => normalizeGeoLabel(x.mandalName) === nm);
    if (hit) {
      fileMandal = hit.mandalName;
      fileVillage = tokens.slice(1).join(" ").trim();
    } else if (left.trim()) {
      fileVillage = left.trim();
    }
  } else if (left.trim()) {
    fileVillage = left.trim();
  }
  if (!fileMandal || !fileVillage) return null;
  const listParts = tail.split(/\s+/);
  let listType = "—";
  let applicationStatus = tail || "—";
  const li = listParts.findIndex((x) => /^list\s*\d*$/i.test(x));
  if (li >= 0) {
    listType = listParts.slice(li, li + 2).join(" ").replace(/\s+/g, " ").trim() || listParts[li];
    applicationStatus = listParts.slice(li + 2).join(" ").trim() || "—";
  }
  return {
    fileMandal,
    fileVillage,
    applicantName,
    mobile,
    aadhaarDigits: aadhaarRaw,
    applicationRef: appRef,
    applicationSerial: serial,
    listType,
    applicationStatus,
  };
}

export function parseLeadExportText(
  raw: string,
  opts?: { defaultDistrictKey?: TelanganaDistrictKey | null; geo?: GeoDirectoryStore | null },
): { districtKey: TelanganaDistrictKey | null; rows: ParsedLeadRow[]; stats: LeadParseStats } {
  const lines = raw.split(/\r?\n/);
  let districtKey: TelanganaDistrictKey | null = opts?.defaultDistrictKey ?? null;
  let currentMandal = "";
  let currentVillage = "";
  const rows: ParsedLeadRow[] = [];
  const geo = opts?.geo ?? null;
  const stats: LeadParseStats = {
    fileLineCount: lines.length,
    rowsAccepted: 0,
    skippedNoAppToken: 0,
    skippedNoMandalVillage: 0,
    skippedShortApplicant: 0,
    skippedRequiredBlank: 0,
    looseLineFallbacks: 0,
  };

  const effectiveDistrictForGeo = (): TelanganaDistrictKey | null => {
    if (districtKey && isTelanganaDistrictKey(districtKey)) return districtKey;
    const d = opts?.defaultDistrictKey;
    return d && isTelanganaDistrictKey(d) ? d : null;
  };

  const mandalNameInDistrict = (name: string): boolean => {
    const dk = effectiveDistrictForGeo();
    if (!geo || !dk) return false;
    const nm = normalizeGeoLabel(name);
    return geo.tgMandalsForDistrict(dk).some((m) => normalizeGeoLabel(m.mandalName) === nm);
  };

  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    if (/^--\s*\d+\s+of\s+\d+\s*--$/i.test(t)) continue;
    if (/^Name of the Mandal/i.test(t)) continue;
    if (/^Sno\s+Application/i.test(t)) continue;

    const dk = districtLineToKey(t);
    if (dk) {
      districtKey = dk;
      continue;
    }

    let cells = mergeSplitApplicationCells(splitLineToCells(t));
    let appIdx = findAppCellIndex(cells);

    if (appIdx < 0) {
      const loose = tryParseLeadLineLoose(
        t,
        { currentMandal, currentVillage },
        geo,
        districtKey && isTelanganaDistrictKey(districtKey) ? districtKey : null,
      );
      if (loose) {
        currentMandal = loose.fileMandal;
        currentVillage = loose.fileVillage;
        rows.push(loose);
        stats.looseLineFallbacks += 1;
      } else {
        stats.skippedNoAppToken += 1;
      }
      continue;
    }

    if (cells.length < 4) {
      stats.skippedNoAppToken += 1;
      continue;
    }

    const joinedAtIdx = parseAppCellFlexible(cells[appIdx])
      ? cells[appIdx]
      : `${cells[appIdx]} ${cells[appIdx + 1]}`.trim();
    const parsed = parseAppCellFlexible(joinedAtIdx);
    if (!parsed) {
      stats.skippedNoAppToken += 1;
      continue;
    }
    if (joinedAtIdx !== cells[appIdx]) {
      cells = [...cells.slice(0, appIdx), joinedAtIdx, ...cells.slice(appIdx + 2)];
    }

    const applicantName = cells[appIdx + 1]?.trim() ?? "";
    const mobile = (cells[appIdx + 2] ?? "").replace(/\D/g, "").slice(0, 15);
    const aadhaarRaw = (cells[appIdx + 3] ?? "").replace(/\D/g, "").slice(0, 12);
    let listType = cells[appIdx + 4]?.trim() ?? "";
    let statusStart = appIdx + 5;
    if (listType && !/^list\s*\d/i.test(listType) && /^list$/i.test(listType) && cells[appIdx + 5] === "1") {
      listType = "List 1";
      statusStart = appIdx + 6;
    }
    const applicationStatus = cells.slice(statusStart).join(" ").trim();

    if (!applicantName || applicantName.length < 1) {
      stats.skippedShortApplicant += 1;
      continue;
    }
    if (!mobile || !aadhaarRaw || !listType || !applicationStatus || !effectiveDistrictForGeo()) {
      stats.skippedRequiredBlank += 1;
      continue;
    }

    const left = cells.slice(0, appIdx);
    if (left.length >= 2) {
      currentMandal = left[0].trim();
      currentVillage = left[1].trim();
    } else if (left.length === 1) {
      const token = left[0].trim();
      const dk = effectiveDistrictForGeo();
      if (geo && dk && mandalNameInDistrict(token)) {
        currentMandal = geo
          .tgMandalsForDistrict(dk)
          .find((m) => normalizeGeoLabel(m.mandalName) === normalizeGeoLabel(token))!.mandalName;
      } else {
        currentVillage = token;
      }
    }

    if (!currentMandal || !currentVillage) {
      stats.skippedNoMandalVillage += 1;
      continue;
    }

    rows.push({
      fileMandal: currentMandal,
      fileVillage: currentVillage,
      applicantName,
      mobile,
      aadhaarDigits: aadhaarRaw,
      applicationRef: parsed.applicationRef,
      applicationSerial: parsed.serial,
      listType,
      applicationStatus,
    });
  }

  stats.rowsAccepted = rows.length;
  return { districtKey, rows, stats };
}

